import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAlert } from '@components/showAlert/useAlertHook';
import { apiErrorMessage } from '@functions/api-error-message';
import { GoalMonth, goalsPayload } from '../sales-goals';
import { SalesGoalInput } from '../services/requests/save-sales-goals.request';
import { SalesGoalsMonthResponse } from '../services/responses/sales-goals.response';
import { SalesGoalsService } from '../services/sales-goals.service';

export const useSalesGoals = (month: GoalMonth) => {
    const { t } = useTranslation();
    const { showAlert } = useAlert();
    const [report, setReport] = useState<SalesGoalsMonthResponse>();
    const [drafts, setDrafts] = useState<SalesGoalInput[]>([]);
    const [loading, setLoading] = useState(true);
    const [savingUserId, setSavingUserId] = useState<string>();
    const requestRef = useRef(0);

    const load = useCallback(async () => {
        const request = ++requestRef.current;
        setLoading(true);
        try {
            const response = await new SalesGoalsService().goals(month.year, month.month);
            if (request !== requestRef.current) return;
            const nextReport = response.data.data;
            setReport(nextReport);
            setDrafts(nextReport.users.map((user) => ({
                userId: user.userId,
                revenueTarget: user.revenueTarget ?? null,
                salesTarget: user.salesTarget ?? null,
            })));
        } catch (error) {
            if (request !== requestRef.current) return;
            showAlert(apiErrorMessage(error), 'error', 4000);
            setReport(undefined);
            setDrafts([]);
        } finally {
            if (request === requestRef.current) setLoading(false);
        }
    }, [month, showAlert]);

    useEffect(() => { void load(); }, [load]);

    const change = (userId: string, patch: Partial<SalesGoalInput>) =>
        setDrafts((rows) => rows.map((row) => row.userId === userId ? { ...row, ...patch } : row));

    const saveUser = async (userId: string, remove = false) => {
        if (!report) return;
        setSavingUserId(userId);
        const selectedDraft = drafts.find((row) => row.userId === userId);
        // Só a linha salva vai: o backend mantém quem não vier, e reenviar os outros desfaria a edição de um colega.
        const [goal] = goalsPayload(month, [{
            userId,
            revenueTarget: remove ? null : selectedDraft?.revenueTarget ?? null,
            salesTarget: remove ? null : selectedDraft?.salesTarget ?? null,
        }]).goals;
        try {
            await new SalesGoalsService().save({ year: month.year, month: month.month, goals: [goal] });
            setReport((current) => current ? {
                ...current,
                users: current.users.map((user) => user.userId === userId
                    ? { ...user, revenueTarget: goal.revenueTarget, salesTarget: goal.salesTarget }
                    : user),
            } : current);
            if (remove) setDrafts((rows) => rows.map((row) => row.userId === userId ? { ...row, revenueTarget: null, salesTarget: null } : row));
            showAlert(t(remove ? 'metas.removido' : 'metas.salvo'), 'success', 2500);
        } catch (error) {
            showAlert(apiErrorMessage(error), 'error', 4000);
        } finally {
            setSavingUserId(undefined);
        }
    };

    return { report, drafts, loading, savingUserId, change, saveUser };
};
